import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ArticleItem from "@/components/feature/ArticleItem";
import EmptyState from "@/components/base/EmptyState";
import LoadingState from "@/components/base/LoadingState";
import { useSeo } from "@/hooks/useSeo";
import { useMockLoading } from "@/hooks/useMockLoading";
import { specials } from "@/mocks/specials";
import { formatDate, getArticleById, getArticlesByIds, sortByDateDesc } from "@/lib/content";

export default function Special() {
  useSeo({
    title: "朋友的妈妈3影视资讯专题 - 最新资讯与深度解读",
    description:
      "朋友的妈妈3影视资讯专题，汇集剧情深度解读、角色档案与官方发布等分组内容，按发布时间梳理最新资讯与幕后故事。",
    keywords: "朋友的妈妈3资讯,朋友的妈妈3专题,朋友的妈妈3解读,朋友的妈妈3官方",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const articleId = searchParams.get("article");
  const activeArticle = articleId ? getArticleById(articleId) : undefined;
  const loading = useMockLoading(450);

  useEffect(() => {
    document.body.style.overflow = activeArticle ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeArticle]);

  const closeArticle = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("article");
    setSearchParams(next);
  };

  return (
    <>
      <section className="relative w-full">
        <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden bg-foreground-950">
          <img
            src={specials[0].cover}
            alt="资讯专题封面"
            title="朋友的妈妈3影视资讯专题"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/45"></div>
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-6 pb-8 md:pb-12">
            <nav className="w-full text-sm text-background-100/60 mb-4" aria-label="面包屑">
              <ol className="flex items-center gap-2 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50">资讯专题</li>
              </ol>
            </nav>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap">
              <i className="ri-newspaper-line"></i>
              深度专题
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50">
              影视资讯专题
            </h1>
            <p className="mt-3 text-sm md:text-base text-background-100/80 max-w-3xl leading-relaxed">
              汇集《朋友的妈妈3》的深度解读、角色档案与官方发布，按主题分组呈现最新资讯与幕后故事。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8 space-y-10">
            {loading ? (
              <LoadingState text="正在加载专题内容…" />
            ) : specials.length === 0 ? (
              <EmptyState title="当前暂无内容" description="资讯专题正在更新中，请稍后再来。" />
            ) : (
              specials.map((special) => (
                <div key={special.id} id={special.id} className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                      {special.title}
                    </h2>
                  </div>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                    {special.intro}
                  </p>

                  <div className="space-y-6">
                    {special.groups.map((group) => {
                      const groupArticles = sortByDateDesc(getArticlesByIds(group.articleIds));
                      return (
                        <div
                          key={group.title}
                          className="rounded-lg bg-background-100 border border-background-200/80 p-4 md:p-6"
                        >
                          <div className="mb-3 pb-3 border-b border-background-200/80">
                            <h3 className="font-medium text-foreground-950">{group.title}</h3>
                            <p className="mt-1 text-xs text-foreground-500">{group.description}</p>
                          </div>
                          <div className="divide-y divide-background-200/70">
                            {groupArticles.map((article) => (
                              <ArticleItem key={article.id} article={article} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg bg-background-100 border border-background-200/80 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-accent-500"></i>
                相关专题推荐
              </h2>
              <ul className="space-y-3">
                {specials.map((special) => (
                  <li key={special.id}>
                    <Link
                      to="/special"
                      className="group flex gap-3 p-2 rounded-md hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <span className="w-20 h-14 rounded-md overflow-hidden shrink-0 bg-foreground-900">
                        <img
                          src={special.cover}
                          alt={special.title}
                          title={special.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {special.title}
                        </span>
                        <span className="block mt-1 text-xs text-foreground-400">
                          {formatDate(special.date)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg bg-primary-950 p-5">
              <h2 className="font-heading text-base font-bold text-background-50 mb-2">
                想看完整剧情？
              </h2>
              <p className="text-sm text-background-100/70 leading-relaxed">
                前往剧情分类页，按章节浏览《朋友的妈妈3》的完整故事线。
              </p>
              <Link
                to="/category"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                浏览剧情分类
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {activeArticle && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center bg-foreground-950/70 p-0 md:p-6 overflow-y-auto"
          onClick={closeArticle}
        >
          <article
            className="relative w-full max-w-3xl bg-background-50 md:rounded-lg my-0 md:my-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeArticle}
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-md bg-background-50/90 text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <div className="w-full h-56 md:h-72 overflow-hidden md:rounded-t-lg bg-foreground-900">
              <img
                src={activeArticle.cover}
                alt={activeArticle.title}
                title={activeArticle.title}
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="p-5 md:p-8">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                {activeArticle.category}
              </span>
              <h2 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                {activeArticle.title}
              </h2>
              <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {formatDate(activeArticle.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {activeArticle.author}
                </span>
              </div>
              <div className="mt-5 space-y-3">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {activeArticle.summary}
                </p>
                {activeArticle.body.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed md:leading-loose">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-background-200/80 flex flex-wrap gap-2">
                {activeArticle.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>
        </div>
      )}
    </>
  );
}